import { useEffect, useState } from "react";
import { FileSignature, Plus, Copy, ShieldOff, FileDown, PenLine } from "lucide-react";
import { toast } from "sonner";
import { Sign } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SignDocumentForm from "@/components/SignDocumentForm";
import SignHistory from "@/components/SignHistory";
import { useAuth } from "@/lib/auth";

const STATUS = {
  pending: ["Pendente", "text-amber-300 border-amber-500/30"], signed: ["Assinado", "text-emerald-300 border-emerald-500/30"],
  revoked: ["Revogado", "text-rose-300 border-rose-500/30"], expired: ["Expirado", "text-slate-400 border-slate-600"],
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "");
const remoteLink = (token) => `${window.location.origin}/sign/${token}`;

const DOC_STATE = { assinado: ["Concluído", "text-emerald-300"], pendente: ["Por assinar · pedido enviado", "text-amber-300"],
  atualizar: ["Requer atualização", "text-amber-300"], em_falta: ["Por assinar", "text-slate-400"],
  expirado: ["Por assinar · pedido expirado", "text-slate-400"], revogado: ["Por assinar · pedido revogado", "text-rose-300"] };
const AUTH_STATE = { aceite: "text-emerald-300", recusada: "text-slate-400", retirada: "text-rose-300" };

function SignStatus({ status, onWithdraw, onRequest }) {
  const auths = Object.entries(status.authorizations);
  return (
    <div className="grid gap-3 sm:grid-cols-2 text-xs" data-testid="sign-status-panel">
      <div className="min-w-0 rounded-xl border border-slate-800 p-3 space-y-1.5">
        {status.documents.map((d) => {
          const [l, c] = d.decision === "recusado" ? ["Não autorizou", "text-rose-300"] : DOC_STATE[d.state];
          return (
            <div key={d.key} className="flex justify-between items-center gap-2" data-testid={`sign-doc-state-${d.key}`}>
              <span className="text-slate-300 break-words min-w-0 flex-1">{d.title}</span>
              <span className={`${c} shrink-0 whitespace-nowrap`}>{l}</span>
              {d.state !== "pendente" && <button onClick={() => onRequest(d.key)} data-testid={`sign-request-new-${d.key}`} title="Pedir nova assinatura" className="text-sky-300 underline shrink-0 whitespace-nowrap">{d.state === "em_falta" ? "Assinar" : "Nova assinatura"}</button>}
            </div>
          );
        })}
      </div>
      <div className="min-w-0 rounded-xl border border-slate-800 p-3 space-y-1.5">
        {auths.length === 0 ? <div className="text-slate-500">Sem autorizações facultativas registadas.</div> : auths.map(([k, a]) => (
          <div key={k} className="flex justify-between gap-2 items-center" data-testid={`sign-auth-${k}`}>
            <span className="text-slate-300 truncate min-w-0 flex-1" title={a.label}>{a.label}</span>
            <span className={`${AUTH_STATE[a.state]} capitalize shrink-0 whitespace-nowrap`}>{a.state}</span>
            {a.state === "aceite" && <button onClick={() => onWithdraw({ ...a, key: k })} data-testid={`sign-withdraw-${k}`} className="text-rose-300 underline shrink-0 whitespace-nowrap">Retirar</button>}
          </div>
        ))}
        <div className="text-[10px] text-slate-500 pt-1">O estado documental não autoriza automaticamente qualquer técnica.</div>
      </div>
    </div>
  );
}

export default function PatientDocuments({ patientId, onStatus }) {
  const { user } = useAuth();
  const remoteReady = !!user?.mio_sign_remote_ready;
  const [rows, setRows] = useState([]);
  const [tpls, setTpls] = useState([]);
  const [tpl, setTpl] = useState("");
  const [mode, setMode] = useState("presencial");
  const [signing, setSigning] = useState(null);
  const [status, setStatus] = useState(null);
  const [hist, setHist] = useState([]);

  const load = async () => {
    try {
      const [r, s, h] = await Promise.all([Sign.list(patientId), Sign.status(patientId), Sign.history(patientId).catch(() => null)]);
      setRows(r); setStatus(s); setHist(h); onStatus?.(s);
    } catch { /* erro tratado globalmente */ }
  };
  useEffect(() => {
    if (status && window.location.hash === "#documentos") document.getElementById("documentos")?.scrollIntoView({ behavior: "smooth" });
  }, [status]);
  const withdraw = async (a) => {
    if (!window.confirm(`Registar a retirada desta autorização?\n\n${a.label}\n\nO registo assinado anterior é preservado.`)) return;
    try { await Sign.withdraw(a.record_id, a.key); toast.success("Retirada registada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Não foi possível registar"); }
  };
  useEffect(() => {
    load();
    Sign.templates().then((t) => { setTpls(t); setTpl(t[0]?.key || ""); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const copy = async (token, created = false) => {
    const prefix = created ? "Pedido remoto criado (válido 7 dias). " : "";
    try { await navigator.clipboard.writeText(remoteLink(token)); toast.success(`${prefix}Link copiado`); }
    catch { toast.message(`${prefix}Link: ${remoteLink(token)}`); }
  };
  const create = async (key = tpl, m = mode) => {
    const cur = status?.documents.find((d) => d.key === key);
    if (cur?.state === "assinado" && !window.confirm("Este documento já está assinado na versão atual. Criar outro pedido?")) return;
    try {
      const r = await Sign.create({ patient_id: patientId, template_key: key, mode: m, days: 7 });
      await load();
      if (m === "presencial") openSign(r.id); else copy(r.token, true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Não foi possível criar o pedido"); }
  };
  const openSign = async (id) => {
    try { setSigning({ id, doc: await Sign.document(id) }); } catch { toast.error("Não foi possível abrir o documento"); }
  };
  const submit = async (data) => {
    try { await Sign.sign(signing.id, data); toast.success("Documento assinado"); setSigning(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha ao assinar"); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revogar este pedido de assinatura?")) return;
    try { await Sign.revoke(id); load(); } catch { toast.error("Não foi possível revogar"); }
  };

  return (
    <section id="documentos" className="scroll-mt-6 rounded-2xl border border-slate-800 bg-slate-900/40 p-6 space-y-4" data-testid="patient-documents">
      <div className="flex flex-wrap items-center gap-2">
        <FileSignature className="w-4 h-4 text-sky-400" />
        <div className="font-display text-lg text-slate-50">Documentos e consentimentos</div>
      </div>
      {status && <SignStatus status={status} onWithdraw={withdraw} onRequest={(k) => create(k, "presencial")} />}
      <div className="flex flex-wrap gap-2">
        <select data-testid="sign-template-select" value={tpl} onChange={(e) => setTpl(e.target.value)}
          className="flex-1 min-w-[200px] rounded-full bg-slate-950 border border-slate-700 text-slate-200 text-xs px-3 py-2">
          {tpls.map((t) => <option key={t.key} value={t.key}>{t.title} (v{t.version})</option>)}
        </select>
        <select data-testid="sign-mode-select" value={mode} onChange={(e) => setMode(e.target.value)}
          className="rounded-full bg-slate-950 border border-slate-700 text-slate-200 text-xs px-3 py-2">
          <option value="presencial">Presencial (neste dispositivo)</option>
          <option value="remoto" disabled={!remoteReady}>{remoteReady ? "Remoto (link, 7 dias)" : "Remoto (indisponível: envio de códigos por configurar)"}</option>
        </select>
        <button data-testid="sign-create-btn" onClick={() => create()} disabled={!tpl}
          className="inline-flex items-center gap-1.5 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 text-xs font-medium px-4 py-2 transition-colors disabled:opacity-50">
          <Plus className="w-3.5 h-3.5" /> Novo pedido
        </button>
      </div>
      {rows.length === 0 ? <div className="text-sm text-slate-500">Sem documentos.</div> : (
        <div className="divide-y divide-slate-800" data-testid="sign-requests-list">
          {rows.map((r) => {
            const [label, cls] = STATUS[r.status] || STATUS.pending;
            return (
              <div key={r.id} className="py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`sign-request-${r.id}`}>
                <div className="flex-1 min-w-[180px]">
                  <div className="text-slate-200">{r.title}</div>
                  <div className="text-xs text-slate-500">v{r.template_version} · {r.mode} · {fmt(r.signed_at || r.created_at)}</div>
                </div>
                <span className={`text-[11px] rounded-full border px-2 py-0.5 ${cls}`} data-testid={`sign-status-${r.id}`}>{label}</span>
                {r.status === "pending" && r.mode === "presencial" && (
                  <button onClick={() => openSign(r.id)} data-testid={`sign-open-${r.id}`} className="text-xs text-sky-300 inline-flex items-center gap-1"><PenLine className="w-3.5 h-3.5" /> Assinar</button>
                )}
                {r.status === "pending" && r.mode === "remoto" && (
                  <button onClick={() => copy(r.token)} data-testid={`sign-copy-${r.id}`} className="text-xs text-sky-300 inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copiar link</button>
                )}
                {r.status === "pending" && (
                  <button onClick={() => revoke(r.id)} data-testid={`sign-revoke-${r.id}`} className="text-xs text-rose-300 inline-flex items-center gap-1"><ShieldOff className="w-3.5 h-3.5" /> Revogar</button>
                )}
                {r.status === "signed" && (
                  <a href={Sign.receiptUrl(r.record_id)} target="_blank" rel="noreferrer" data-testid={`sign-receipt-${r.id}`} className="text-xs text-emerald-300 inline-flex items-center gap-1"><FileDown className="w-3.5 h-3.5" /> Comprovativo</a>
                )}
              </div>
            );
          })}
        </div>
      )}
      <SignHistory patientId={patientId} rows={hist} />
      <Dialog open={!!signing} onOpenChange={(o) => !o && setSigning(null)}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto bg-slate-950 border-slate-800" data-testid="sign-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-50">Assinatura presencial</DialogTitle>
            <DialogDescription className="text-slate-400">Entregue o dispositivo ao paciente para ler e assinar.</DialogDescription>
          </DialogHeader>
          {signing && <SignDocumentForm doc={signing.doc} onSubmit={submit} identityRequired />}
        </DialogContent>
      </Dialog>
    </section>
  );
}
